import React, { useState } from "react";
import { submitBugReport } from "../Backend/website";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";

const BugsPage = () => {
  const [loader, setLoader] = useState(false);
  const [bugData, setBugData] = useState({
    title: "",
    severity: "Medium",
    environment: "",
    steps: "",
    expected: "",
    actual: "",
  });

  const handleChange = (e) => {
    setBugData({ ...bugData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoader(true);
    const result = await submitBugReport(bugData);
    setLoader(false);

    if (result.success) {
      Swal.fire({
        title: "Report Received",
        text: "Our engineering team has been notified and is on the case.",
        icon: "success",
        confirmButtonColor: "#E63946",
        background: "#ffffff",
        customClass: { popup: "rounded-[2.5rem]" },
      });
      setBugData({
        title: "",
        severity: "Medium",
        environment: "",
        steps: "",
        expected: "",
        actual: "",
      });
    }
  };

  return (
    <div className="bg-white min-h-screen font-sans selection:bg-[#E63946] selection:text-white overflow-x-hidden">

      {/* 1. HERO SECTION - Fixed for Visibility */}
      <section className="relative pt-28 sm:pt-32 pb-12 sm:pb-16 lg:pb-20 bg-[#0B1C2D] overflow-hidden">
        {/* Subtle Background Red Dot Pattern */}
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "radial-gradient(#E63946 0.5px, transparent 0.5px)",
              backgroundSize: "30px 30px",
            }}
          ></div>
        </div>

        <div className="mx-auto w-[95%] md:w-[90%] relative z-10">
          <Link
            to="/"
            className="inline-flex items-center text-sm font-bold text-[#E63946] mb-8 group animate-fade-in-up"
          >
            <span className="material-symbols-outlined text-sm mr-2 group-hover:-translate-x-1 transition-transform">
              arrow_back
            </span>
            Return to Homepage
          </Link>
          <br />
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-[#E63946] text-[10px] font-black uppercase tracking-widest mb-6 animate-fade-in-up">
            <span className="material-symbols-outlined text-xs">terminal</span>
            Debug Center
          </div>
          <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white tracking-tighter leading-none mb-4 sm:mb-6 animate-fade-in-up">
            Report a <span className="text-[#E63946]">Glitch.</span>
          </h1>
          <p className="max-w-xl text-base sm:text-lg md:text-xl text-slate-400 font-medium animate-fade-in-up leading-relaxed">
            Spotted something broken? Help us maintain the{" "}
            <span className="text-white">99.9% uptime</span> standard for the
            ContactHQ community.
          </p>
        </div>
      </section>

      {/* 2. MAIN INTERFACE: FORM & SIDEBAR */}
      <section className="py-8 sm:py-12 bg-slate-50 border-t border-[#E2E8F0]">
        <div className="mx-auto w-[95%] md:w-[90%]">
          <div className="flex flex-col xl:flex-row gap-8 xl:gap-12">
            {/* BUG REPORT FORM */}
            <div className="xl:w-2/3">
              <div className="bg-white p-5 sm:p-6 md:p-10 rounded-2xl sm:rounded-[2.5rem] border border-[#E2E8F0] shadow-xl shadow-slate-200/40">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Title & Severity */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="md:col-span-2 space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-[#0B1C2D]">
                        Bug Summary
                      </label>
                      <input
                        name="title"
                        required
                        value={bugData.title}
                        onChange={handleChange}
                        placeholder="e.g., Lead export failing on CSV"
                        className="w-full px-5 py-3 rounded-xl bg-slate-50 border-2 border-transparent focus:border-[#E63946] outline-none transition-all text-sm font-medium"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-[#0B1C2D]">
                        Severity
                      </label>
                      <select
                        name="severity"
                        value={bugData.severity}
                        onChange={handleChange}
                        className="w-full px-5 py-3 rounded-xl bg-slate-50 border-2 border-transparent focus:border-[#E63946] outline-none text-sm font-bold cursor-pointer"
                      >
                        <option>Low</option>
                        <option>Medium</option>
                        <option>High</option>
                        <option>Critical</option>
                      </select>
                    </div>
                  </div>

                  {/* Environment & Expected Result */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-[#0B1C2D]">
                        Environment
                      </label>
                      <input
                        name="environment"
                        required
                        value={bugData.environment}
                        onChange={handleChange}
                        placeholder="Chrome 120 / Windows 11"
                        className="w-full px-5 py-3 rounded-xl bg-slate-50 border-2 border-transparent focus:border-[#E63946] outline-none text-sm font-medium"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-black uppercase tracking-widest text-[#0B1C2D]">
                        Expected Result
                      </label>
                      <input
                        name="expected"
                        required
                        value={bugData.expected}
                        onChange={handleChange}
                        placeholder="Modal should close after save"
                        className="w-full px-5 py-3 rounded-xl bg-slate-50 border-2 border-transparent focus:border-green-500 outline-none text-sm font-medium"
                      />
                    </div>
                  </div>

                  {/* Reproduction Steps */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-[#0B1C2D]">
                      Steps to Reproduce
                    </label>
                    <textarea
                      name="steps"
                      required
                      rows="3"
                      value={bugData.steps}
                      onChange={handleChange}
                      placeholder="1. Go to Settings... 2. Change name... 3. Click save..."
                      className="w-full px-5 py-3 rounded-xl bg-slate-50 border-2 border-transparent focus:border-[#E63946] outline-none text-sm font-medium resize-none"
                    ></textarea>
                  </div>

                  {/* Actual Error */}
                  <div className="space-y-2">
                    <label className="text-[10px] font-black uppercase tracking-widest text-red-600">
                      Actual Result / Error Message
                    </label>
                    <textarea
                      name="actual"
                      required
                      rows="2"
                      value={bugData.actual}
                      onChange={handleChange}
                      placeholder="UI freezes and console shows 'Uncaught TypeError'..."
                      className="w-full px-5 py-3 rounded-xl bg-slate-50 border-2 border-transparent focus:border-red-500 outline-none text-sm font-medium resize-none"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    disabled={loader}
                    className="w-full py-4 bg-[#0B1C2D] text-white font-black rounded-xl hover:bg-[#E63946] transition-all flex items-center justify-center gap-3 group"
                  >
                    {loader ? (
                      <div className="size-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <>
                        Log Technical Report{" "}
                        <span className="material-symbols-outlined text-sm group-hover:translate-x-1 transition-transform">
                          send
                        </span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* SIDEBAR */}
            <div className="xl:w-1/3 space-y-6">
              <div className="p-8 rounded-[2rem] bg-[#0B1C2D] text-white relative overflow-hidden">
                <p className="text-[10px] font-black uppercase tracking-widest text-[#E63946] mb-4">
                  Current Status
                </p>
                <div className="flex items-end gap-2 mb-6">
                  <span className="text-5xl font-black">94%</span>
                  <span className="text-xs text-slate-400 mb-2 font-bold">
                    System Stability
                  </span>
                </div>
                <div className="w-full h-1.5 bg-white/10 rounded-full overflow-hidden">
                  <div className="w-[94%] h-full bg-[#E63946] animate-pulse"></div>
                </div>
              </div>

              <div className="p-8 rounded-[2rem] bg-white border border-[#E2E8F0]">
                <h3 className="text-sm font-black text-[#0B1C2D] uppercase tracking-widest mb-6">
                  Recent Fixes
                </h3>
                <div className="space-y-6">
                  {[
                    {
                      bug: "Payment Modal Lag",
                      status: "Fixed",
                      color: "text-green-500",
                    },
                    {
                      bug: "Mobile Nav Padding",
                      status: "Fixed",
                      color: "text-green-500",
                    },
                    {
                      bug: "CSV Export Logic",
                      status: "Triaged",
                      color: "text-blue-500",
                    },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between group"
                    >
                      <p className="text-sm font-bold text-[#64748B]">
                        {item.bug}
                      </p>
                      <span
                        className={`text-[10px] font-black uppercase ${item.color}`}
                      >
                        {item.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. LIFECYCLE SECTION */}
      <section className="py-12 sm:py-16 lg:py-24 bg-white overflow-hidden">
        <div className="mx-auto w-[95%] md:w-[90%]">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-5xl font-black text-[#0B1C2D] tracking-tight mb-4">
              The Fix <span className="text-[#E63946]">Lifecycle.</span>
            </h2>
            <p className="text-[#64748B] font-medium max-w-2xl mx-auto">
              Transparent tracking from your initial report to our production
              deploy.
            </p>
          </div>

          <div className="relative flex flex-col md:flex-row justify-between gap-8 md:gap-0 max-w-5xl mx-auto">
            {/* Connector Line */}
            <div className="hidden md:block absolute top-10 left-0 w-full h-0.5 bg-slate-100 -z-10">
              <div className="w-1/3 h-full bg-[#E63946] animate-line-flow"></div>
            </div>

            {[
              {
                title: "Triage",
                icon: "assignment_late",
                desc: "Report received & verified",
              },
              {
                title: "Isolation",
                icon: "bug_report",
                desc: "Isolated in debug sandbox",
              },
              {
                title: "Deployment",
                icon: "verified",
                desc: "Push to production edge",
              },
            ].map((step, i) => (
              <div
                key={i}
                className="flex flex-col items-center text-center md:w-1/3 group"
              >
                <div className="size-20 rounded-3xl bg-slate-50 border border-[#E2E8F0] flex items-center justify-center text-[#0B1C2D] mb-6 group-hover:bg-[#E63946] group-hover:text-white group-hover:-translate-y-2 transition-all duration-500 shadow-sm">
                  <span className="material-symbols-outlined text-3xl">
                    {step.icon}
                  </span>
                </div>
                <h4 className="text-xl font-black text-[#0B1C2D] mb-2">
                  {step.title}
                </h4>
                <p className="text-[10px] text-[#64748B] font-bold uppercase tracking-widest">
                  {step.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <style jsx>{`
        @keyframes lineFlow {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(300%);
          }
        }
        .animate-line-flow {
          animation: lineFlow 4s infinite linear;
        }
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fade-in-up {
          animation: fadeInUp 0.8s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default BugsPage;
